import { getReportLifecycleStatus } from "@utils/reportStatus";
import { Navigate, useLocation } from "react-router-dom";

import {
  ApiReportReportStatusChoices,
  ApiReportSectionsAiGeneratedStatusChoices,
  ReportSectionsType,
  ReportStatusExtendedEnums,
} from "@api/graphql/types-and-hooks";
import { StatusType } from "@components/ClinicalReportsTable/ClinicalReportsTable";
import { useClinicalReportDetailsData } from "@providers/ClinicalReportDetailsProvider";
import { ReportNode, useReport } from "@providers/ReportProvider";

export const ClinicalReportRedirect = () => {
  const location = useLocation();
  if (
    location.pathname.includes("/reassessment") ||
    location.pathname.includes("/evaluations") ||
    location.pathname.includes("/assessment-reports")
  ) {
    return null;
  }

  const newPath = location.pathname.replace(/^\/clinical-reports/, "/clinical-reports/reassessment");

  return <Navigate to={`${newPath}${location.search}`} replace />;
};

export const ClinicalReportDetailsRedirect = () => {
  const { state } = useLocation();
  const { isReassessment } = useClinicalReportDetailsData();
  const { report } = useReport();
  const assessment = report;

  const pathToRedirectTo = getStatusAndRedirectRouteFromReport(assessment, {
    isReassessment,
  }).redirectRoute;

  return <Navigate to={pathToRedirectTo} replace state={state} />;
};

export type ClinicalReportPageRoute =
  | "upload-files"
  | "refreshing-data"
  | "select-programming"
  | "report-page"
  | "review-and-sign";

export const getStatusAndRedirectRouteFromReport = (
  report?: ReportNode,
  options?: { isReassessment?: boolean },
): { status: StatusType; redirectRoute: ClinicalReportPageRoute } => {
  const isReassessment = options?.isReassessment ?? false;
  const getFilteredSections = (location: string): ReportSectionsType[] =>
    report?.reportSections?.edges
      ?.map((edge) => edge?.node)
      .filter((section): section is ReportSectionsType => !!section && section.pageLocation === location) ?? [];

  const isSectionErrored = (sections: ReportSectionsType[]): boolean =>
    sections.some((section) => section.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.Failure);

  const isSectionGenerated = (sections: ReportSectionsType[]): boolean =>
    sections.some(
      (section) =>
        section.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.SectionPredictionPredicted ||
        section.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.Pending,
    );

  const isSectionGenerating = (sections: ReportSectionsType[]): boolean =>
    sections.some(
      (section) => section.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.SectionPredictionPending,
    );

  const isSectionPending = (sections: ReportSectionsType[]): boolean =>
    sections.length === 0 ||
    sections.every((section) => section.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.Pending);

  const reportFiles = report?.reportFiles?.edges?.map((edge) => edge?.node) ?? [];
  const page1Sections = getFilteredSections("report-part-1");

  type StatusRule = {
    condition: () => boolean;
    result: { status: StatusType; redirectRoute: ClinicalReportPageRoute };
  };

  const statusRules: StatusRule[] = [
    {
      // Completed Reassessments mirror Completed Initial Assessments: land on the
      // review step (locked, no suggestions) instead of the goal-progress page.
      condition: () => isReassessment && report?.reportStatus === ApiReportReportStatusChoices.Completed,
      result: { status: getReportLifecycleStatus(report), redirectRoute: "review-and-sign" },
    },
    {
      // Once the report reaches Completed, surface the payor submission lifecycle
      // (Submitted / Needs Revision / Approved) when a payor submission exists,
      // and fall back to "Complete" when it doesn't.
      condition: () => report?.reportStatus === ApiReportReportStatusChoices.Completed,
      result: { status: getReportLifecycleStatus(report), redirectRoute: "report-page" },
    },
    {
      condition: () => isReassessment && report?.extendedStatus === ReportStatusExtendedEnums.ImportingClientData,
      result: { status: "Refreshing data\u2026", redirectRoute: "refreshing-data" },
    },
    {
      condition: () => isReassessment && report?.extendedStatus === ReportStatusExtendedEnums.ImportFailed,
      result: { status: "Import Failed", redirectRoute: "refreshing-data" },
    },
    {
      condition: () => isReassessment && report?.extendedStatus === ReportStatusExtendedEnums.AwaitingGoalSelection,
      result: { status: "Draft", redirectRoute: "select-programming" },
    },
    {
      condition: () =>
        isSectionErrored(page1Sections) ||
        isSectionGenerating(page1Sections) ||
        isSectionGenerated(page1Sections) ||
        page1Sections.length > 0 ||
        report?.reportStatus === ApiReportReportStatusChoices.Generating ||
        report?.extendedStatus === ReportStatusExtendedEnums.ImportFailed,
      result: { status: "Draft", redirectRoute: "report-page" },
    },
    {
      condition: () => reportFiles.length === 0,
      result: { status: "Draft", redirectRoute: "upload-files" },
    },
    {
      condition: () => reportFiles.length > 0 && isSectionPending(page1Sections),
      result: { status: "Draft", redirectRoute: "upload-files" },
    },
  ];

  // Find first matching rule or return default
  const defaultResult: { status: StatusType; redirectRoute: ClinicalReportPageRoute } = {
    status: "Draft",
    redirectRoute: "upload-files",
  };

  return statusRules.find((rule) => rule.condition())?.result ?? defaultResult;
};
